import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BasicCli from '../../../main/components/basicCli/BasicCli';
import RoomAvailability from '../RoomAvailability/RoomAvailability';
import CreateReservationModal from '../CreateReservationModal/CreateReservationModal';
import Reservations from '../Reservations/Reservations';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import { toast } from 'react-toastify';
import IHotel from '../../../main/interfaces/IHotel';
import { useTranslation } from 'react-i18next';
import '../Hotel/Hotel.css';
import '../HotelShared/HotelShared.css';
import { IReservationViewModel } from '../Reservations/Reservations';

interface Room {
  roomId: number;
  roomNumber: string;
  roomTypeId: number;
  roomTypeName: string;
  status?: number | null;
  pricePerNight?: number | null;
  totalPrice?: number | null;
  recommendedRatePlanId?: number | null;
  recommendedRatePlanName?: string | null;
  rateOptions?: Array<{
    roomRateId: number;
    ratePlanId: number;
    ratePlanName: string;
    pricePerNight: number;
    totalPrice: number;
    nightCount: number;
    isRefundable: boolean;
  }>;
}

const resolveGuestName = (item: any) => {
  const directName =
    item?.guestName ??
    item?.GuestName ??
    item?.primaryGuestName ??
    item?.PrimaryGuestName;

  if (typeof directName === 'string' && directName.trim()) {
    return directName.trim();
  }

  const nestedGuest = item?.guest ?? item?.Guest ?? item?.primaryGuest ?? item?.PrimaryGuest;
  const firstName = nestedGuest?.firstName ?? nestedGuest?.FirstName ?? '';
  const lastName = nestedGuest?.lastName ?? nestedGuest?.LastName ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName) {
    return fullName;
  }

  const guestList = Array.isArray(item?.guests)
    ? item.guests
    : Array.isArray(item?.Guests)
      ? item.Guests
      : [];

  const primaryGuest =
    guestList.find((guest: any) => guest?.isPrimary || guest?.IsPrimary) ??
    guestList[0];

  if (!primaryGuest) {
    return '';
  }

  const guestItem = primaryGuest?.guest ?? primaryGuest?.Guest ?? primaryGuest;
  return `${guestItem?.firstName ?? guestItem?.FirstName ?? ''} ${guestItem?.lastName ?? guestItem?.LastName ?? ''}`.trim();
};

const HotelCalendarPage: React.FC = () => {
  const { i18n } = useTranslation();
  const [hotels, setHotels] = useState<IHotel[]>([]);
  const [selectedHotelId, setSelectedHotelId] = useState<number | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [checkInDate, setCheckInDate] = useState<string>('');
  const [checkOutDate, setCheckOutDate] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [reservations, setReservations] = useState<IReservationViewModel[]>([]);

  useEffect(() => {
    const loadHotels = async () => {
      try {
        const res = await axiosInstance.get(ApiConstants.hotel.hotels);
        const hotelItems = Array.isArray(res.data) ? res.data : [];

        setHotels(hotelItems);

        if (hotelItems.length > 0) {
          setSelectedHotelId((prev) => prev ?? hotelItems[0].id);
        }
      } catch {
        toast.error(i18n.t('reservationDesk.failedToLoadHotels'));
      }
    };

    loadHotels();
  }, [i18n]);

  useEffect(() => {
    const loadReservations = async () => {
      if (!selectedHotelId) {
        setReservations([]);
        return;
      }

      try {
        const [reservationsResponse, roomsResponse] = await Promise.all([
          axiosInstance.post(ApiConstants.reservation.listCt, {
            gridItems: [],
            pagination: {
              page: 1,
              itemPerPage: 500,
              count: 0,
              itemsPerPage: [10, 20, 50, 100],
            },
          }),
          axiosInstance.get(ApiConstants.hotel.rooms),
        ]);

        const reservationItems = Array.isArray(reservationsResponse.data)
          ? reservationsResponse.data
          : Array.isArray(reservationsResponse.data?.items)
            ? reservationsResponse.data.items
            : [];
        const roomItems = Array.isArray(roomsResponse.data) ? roomsResponse.data : [];
        const roomLookup = new Map(
          roomItems.map((room: any) => [
            Number(room.id ?? room.Id ?? 0),
            {
              roomNumber: room.roomNumber ?? room.RoomNumber,
              status: Number(room.status ?? room.Status ?? 1),
            },
          ]),
        );

        setReservations(
          reservationItems
            .filter((item: any) => {
              const itemHotelId = Number(item.hotelId ?? item.HotelId ?? 0);
              return itemHotelId ? itemHotelId === selectedHotelId : true;
            })
            .map((item: any) => ({
              id: Number(item.id ?? item.Id ?? 0),
              hotelId: Number(item.hotelId ?? item.HotelId ?? selectedHotelId ?? 0),
              roomId: Number(item.roomId ?? item.RoomId ?? 0),
              roomNumber:
                item.roomNumber ??
                item.RoomNumber ??
                roomLookup.get(Number(item.roomId ?? item.RoomId ?? 0))?.roomNumber,
              roomStatus: roomLookup.get(Number(item.roomId ?? item.RoomId ?? 0))?.status,
              checkInDate: item.checkInDate ?? item.CheckInDate ?? '',
              checkOutDate: item.checkOutDate ?? item.CheckOutDate ?? '',
              status: Number(item.status ?? item.Status ?? 1),
              guestName: resolveGuestName(item),
              lockedPriceTotal: Number(item.lockedPriceTotal ?? item.LockedPriceTotal ?? 0),
              depositAmount: Number(item.depositAmount ?? item.DepositAmount ?? 0),
              balanceDue: Number(item.balanceDue ?? item.BalanceDue ?? 0),
            })),
        );
      } catch {
        toast.error(i18n.t('reservationDesk.failedToLoadReservations'));
      }
    };

    loadReservations();
  }, [i18n, refreshKey, selectedHotelId]);

  const handleRoomSelect = useCallback((room: Room, nextCheckInDate: string, nextCheckOutDate: string) => {
    setSelectedRoom(room);
    setCheckInDate(nextCheckInDate);
    setCheckOutDate(nextCheckOutDate);
  }, []);

  const handleDatesChange = useCallback((nextCheckInDate: string, nextCheckOutDate: string) => {
    setCheckInDate(nextCheckInDate);
    setCheckOutDate(nextCheckOutDate);
  }, []);

  const filteredReservations = useMemo(() => {
    return [...reservations].sort((left, right) => {
      const leftCheckIn = new Date(left.checkInDate).getTime();
      const rightCheckIn = new Date(right.checkInDate).getTime();

      if (Number.isNaN(leftCheckIn) || Number.isNaN(rightCheckIn)) {
        return right.id - left.id;
      }

      return rightCheckIn - leftCheckIn;
    });
  }, [reservations]);

  const selectedHotel = useMemo(() => hotels.find((hotel) => Number(hotel.id) === Number(selectedHotelId)) ?? null, [hotels, selectedHotelId]);

  return (
    <BasicCli>
      <div className="hotelSectionPage hotel-dashboard hotel-booking-page">
        <div className="ui-panel hotelSectionHeader hotel-header hotel-booking-hero">
          <div className="hotelSectionHeading">
            <h1>{i18n.t('reservationDesk.dashboardTitle')}</h1>
          </div>
        </div>

        <div className="bcCard hotelSectionCard hotel-panel hotel-booking-search-card">
          <RoomAvailability
            hotelId={selectedHotelId}
            hotels={hotels}
            onHotelChange={setSelectedHotelId}
            onRoomSelect={handleRoomSelect}
            onDatesChange={handleDatesChange}
            refreshKey={refreshKey}
          />
        </div>

        <div className="bcCard hotelSectionCard hotel-panel hotel-booking-calendar-card">
          <Reservations
            embedded
            hotelId={selectedHotelId}
            reservations={filteredReservations}
            onRefresh={() => setRefreshKey((current) => current + 1)}
            hotelCheckInTime={selectedHotel?.checkInTime}
            hotelCheckOutTime={selectedHotel?.checkOutTime}
          />
        </div>

        {selectedRoom && selectedHotelId && (
          <CreateReservationModal
            hotelId={selectedHotelId}
            roomId={selectedRoom.roomId}
            roomNumber={selectedRoom.roomNumber}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            rateOptions={selectedRoom.rateOptions ?? []}
            onSaved={() => setRefreshKey((current) => current + 1)}
            onClose={() => setSelectedRoom(null)}
          />
        )}
      </div>
    </BasicCli>
  );
};

export default HotelCalendarPage;
